// API client — thin fetch wrapper with:
//   · access token in memory only (never localStorage)
//   · auto-refresh on 401 via /auth/refresh (uses httpOnly cookie)
//   · typed error envelope
//
// Access token lifetime: 10 min. On expiry, next request 401s → we call
// refresh, get new access token, retry once.

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api'

// ─── Access token store (in-memory only) ───────────────────────────
let accessToken: string | null = null
let refreshInFlight: Promise<string | null> | null = null
/**
 * Gọi sau mỗi lần /auth/refresh thành công giữa chừng (401 → refresh). auth.ts đăng ký để cập nhật danh sách đơn vị
 * (loại đơn vị có thể vừa đổi — docs/KE_HOACH_NANG_CAP_DON_VI.md §3.8). Tách callback để api.ts không import auth.ts.
 */
let onRefreshed: ((json: Record<string, unknown>) => void) | null = null
export function setRefreshListener(fn: ((json: Record<string, unknown>) => void) | null) {
  onRefreshed = fn
}

/**
 * Máy chủ từ chối vì quyền nhân viên (403 'staff.*', 'auth.must_change_password' — docs/KE_HOACH_THANH_VIEN_DON_VI.md):
 * quyền vừa đổi / bị khoá / bị gỡ; hoặc trang gọi việc của giao diện đơn vị không có (403 'unit.mode_off' —
 * docs/KE_HOACH_CHON_GIAO_DIEN_CAP_DUOI.md). auth.ts đăng ký để đọc lại quyền / giao diện và vẽ lại menu.
 */
let onStaffDenied: ((code: string) => void) | null = null
export function setStaffDeniedListener(fn: ((code: string) => void) | null) {
  onStaffDenied = fn
}

/** Đơn vị web đang hiện — gửi kèm khi làm mới phiên để token mới đúng đơn vị đó (phiên cũ chưa ghi đơn vị đang mở). */
let refreshHint: (() => string | null) | null = null
export function setRefreshHint(fn: (() => string | null) | null) {
  refreshHint = fn
}

export function setAccessToken(token: string | null) {
  accessToken = token
}
export function getAccessToken() {
  return accessToken
}

// ─── Typed errors ──────────────────────────────────────────────────
export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public statusCode: number,
    public details?: unknown,
    public requestId?: string,
  ) {
    super(message)
  }
}

// ─── Core fetch ────────────────────────────────────────────────────
async function raw(input: RequestInfo, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers)
  if (accessToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${accessToken}`)
  }
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  return fetch(input, { ...init, headers, credentials: 'include' })
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

/** Khoảng chờ server yêu cầu khi bị giới hạn tần suất, tính bằng ms. */
async function retryAfterMs(res: Response): Promise<number> {
  const header = Number(res.headers.get('retry-after'))
  if (Number.isFinite(header) && header > 0) return header * 1000
  try {
    const body = await res.clone().json()
    const s = Number(body?.error?.details?.retryAfterSeconds)
    if (Number.isFinite(s) && s > 0) return s * 1000
  } catch {}
  return 1000
}

async function tryRefresh(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight
  refreshInFlight = (async () => {
    try {
      const hint = refreshHint?.() ?? null
      const init = (): RequestInit => (hint ? { method: 'POST', body: JSON.stringify({ activeShopId: hint }) } : { method: 'POST' })
      let res = await raw(`${API_BASE}/auth/refresh`, init())
      // 429 KHÔNG có nghĩa là phiên đã hết hạn — chỉ là đang bị giới hạn tần
      // suất. Trả null ở đây sẽ đá người dùng về màn đăng nhập dù phiên của họ
      // vẫn còn hiệu lực. Chờ đúng khoảng server yêu cầu rồi thử lại một lần.
      if (res.status === 429) {
        await sleep(Math.min(await retryAfterMs(res), 5_000))
        res = await raw(`${API_BASE}/auth/refresh`, init())
      }
      if (!res.ok) return null
      const json = await res.json()
      accessToken = json.accessToken as string
      try {
        onRefreshed?.(json)
      } catch {}
      return accessToken
    } finally {
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}

/**
 * Làm mới access token bằng đúng luồng refresh single-flight ở trên (đang có lượt làm mới thì dùng chung). Cho client
 * gọi dịch vụ khác bằng token ShopTrack — Gom Đơn (features/gomdon) — tự lấy token mới khi bị báo hết hạn.
 * Trả token mới; null nếu không làm mới được (phiên đã hết, lỗi mạng).
 */
export function refreshAccessToken(): Promise<string | null> {
  return tryRefresh().catch(() => null)
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  let res = await raw(`${API_BASE}${path}`, init)
  // 401 của đăng nhập / mã email là "sai mật khẩu / sai mã" — refresh rồi thử lại sẽ xoay
  // mất phiên đang chờ mã (kẹt ở màn nhập mã). Chỉ refresh cho API thường.
  const isAuthStep = /^\/auth\/(login|refresh|2fa\/verify|2fa\/resend)$/.test(path)
  if (res.status === 401 && accessToken && !isAuthStep) {
    const refreshed = await tryRefresh()
    if (refreshed) {
      res = await raw(`${API_BASE}${path}`, init)
    }
  }
  const text = await res.text()
  const json = text ? JSON.parse(text) : null
  if (!res.ok) {
    const e = json?.error ?? {}
    if (typeof e.code === 'string' && (e.code.startsWith('staff.') || e.code === 'auth.must_change_password' || e.code === 'unit.mode_off')) {
      try {
        onStaffDenied?.(e.code)
      } catch {}
    }
    let msg = e.message || `HTTP ${res.status}`
    if (typeof e.details === 'string' && e.details.trim()) {
      msg = e.details.trim()
    } else if (Array.isArray(e.details) && e.details[0]?.message) {
      msg = e.details[0].message
    }
    throw new ApiError(
      e.code ?? 'unknown',
      msg,
      res.status,
      e.details,
      e.requestId,
    )
  }
  return json as T
}

// ─── Convenience methods ───────────────────────────────────────────
export const get = <T>(p: string) => api<T>(p)
export const post = <T>(p: string, body?: unknown) =>
  api<T>(p, { method: 'POST', body: body ? JSON.stringify(body) : undefined })
export const put = <T>(p: string, body: unknown) =>
  api<T>(p, { method: 'PUT', body: JSON.stringify(body) })
export const patch = <T>(p: string, body?: unknown) =>
  api<T>(p, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined })
export const del = <T>(p: string) => api<T>(p, { method: 'DELETE' })
export const uploadFile = <T>(p: string, formData: FormData) =>
  api<T>(p, { method: 'POST', body: formData })

/** Trigger a CSV download by fetching with auth header, then blob-download. */
export async function downloadCsv(path: string, filename?: string): Promise<void> {
  const token = getAccessToken()
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    headers: token ? { authorization: `Bearer ${token}` } : {},
  })
  if (!res.ok) throw new Error(`Tải CSV thất bại (${res.status})`)
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename ?? path.split('/').pop() ?? 'export.csv'
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/**
 * Lấy file (PDF / zip vận đơn…) có xác thực, giống api(): 401 thì làm mới phiên rồi thử lại; lỗi máy chủ
 * (JSON) ném đúng thông báo — không nuốt thành "HTTP 400".
 */
export async function fetchFile(path: string, body?: unknown): Promise<Blob> {
  // Có body → POST JSON (danh sách mã dài không nhét được vào URL)
  const init = (): RequestInit => (body === undefined ? {} : { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) })
  let res = await raw(`${API_BASE}${path}`, init())
  if (res.status === 401 && accessToken && (await tryRefresh())) res = await raw(`${API_BASE}${path}`, init())
  if (!res.ok) {
    const json = await res.json().catch(() => null)
    const e = json?.error ?? {}
    throw new ApiError(e.code ?? 'unknown', e.message || `Tải file thất bại (${res.status})`, res.status, e.details, e.requestId)
  }
  return res.blob()
}

/** Tải file về máy (fetchFile + lưu với tên `filename`). */
export async function downloadFile(path: string, filename: string, body?: unknown): Promise<void> {
  const blob = await fetchFile(path, body)
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
